import { useMemo } from "react";
import { CheckCircle2, RefreshCw, ShieldCheck } from "lucide-react";

import type { DocgenResponse } from "@/api/schemas";

/**
 * P2 — after an "Ažuriraj (samo izmjene)" run, summarise what the amend
 * touched: which inputs changed, which sections were regenerated, and which
 * were kept because the architect locked / hand-edited them. Renders nothing
 * on a fresh full generation (no ``amendment_plan``).
 */
export function AmendmentSummary({ data }: { data: DocgenResponse }) {
  const plan = data.amendment_plan;

  const titleById = useMemo(() => {
    const map = new Map<string, string>();
    for (const s of [
      ...data.izjave,
      ...data.naslovnice,
      ...data.tekstualni_dio,
      ...data.tables,
    ]) {
      map.set(s.section_id, s.title);
    }
    return map;
  }, [data]);

  if (!plan) return null;

  const label = (id: string) => titleById.get(id) ?? id;

  if (!plan.has_changes) {
    return (
      <div className="mb-4 flex items-center gap-2 rounded-md border border-stone-200 bg-stone-50 px-4 py-3 text-[0.85rem] text-stone-600">
        <CheckCircle2 className="h-4 w-4 text-stone-400" />
        Ažurirano — ulazni podaci se nisu promijenili, dokument je ostao isti.
      </div>
    );
  }

  return (
    <div className="mb-4 rounded-md border border-ind-200 bg-ind-50/60 px-4 py-3 text-[0.85rem]">
      <div className="flex items-center gap-2 font-semibold text-ind-800">
        <RefreshCw className="h-4 w-4" />
        Dokumentacija ažurirana
      </div>

      {plan.changed_inputs.length > 0 && (
        <p className="mt-1.5 text-stone-600">
          Promijenjeni podaci:{" "}
          <span className="font-medium text-stone-800">
            {plan.changed_inputs.join(", ")}
          </span>
        </p>
      )}

      {plan.regenerate.length > 0 && (
        <div className="mt-2">
          <span className="text-stone-500">Regenerirano:</span>
          <ul className="mt-1 flex flex-col gap-0.5">
            {plan.regenerate.map((id) => (
              <li key={id} className="text-stone-800">
                • {label(id)}
              </li>
            ))}
          </ul>
        </div>
      )}

      {plan.protected.length > 0 && (
        <div className="mt-2">
          <span className="inline-flex items-center gap-1 text-amber-700">
            <ShieldCheck className="h-3.5 w-3.5" />
            Zadržano (zaključano / ručno uređeno) iako su se podaci promijenili:
          </span>
          <ul className="mt-1 flex flex-col gap-0.5">
            {plan.protected.map((id) => (
              <li key={id} className="text-stone-800">
                • {label(id)}
              </li>
            ))}
          </ul>
        </div>
      )}

      <p className="mt-2 text-[0.78rem] text-stone-400">
        {plan.unchanged.length} sekcija nepromijenjeno.
      </p>
    </div>
  );
}
